import { FC, HTMLAttributes, JSX } from 'react';
import { IconType } from 'react-icons';
import { HiOutlineChevronRight } from 'react-icons/hi';
import { twMerge } from 'tailwind-merge';
import { merge } from '../../../helpers/Utility';
import { Icon, Theme } from '../icons/Icon';
import { CardActionButton, CardHeader, CardIconButton } from './CardHeader';

interface TSTitleCardProps extends HTMLAttributes<HTMLDivElement> {
  descriptor?: string;
  title: string;
  icon?: IconType;
  smallContent?: JSX.Element;
  largeContent?: JSX.Element;
  primaryAction?: CardIconButton;
  secondaryAction?: CardActionButton;
  theme?: Theme;
  longString?: boolean;
}

interface TSCardProps extends HTMLAttributes<HTMLDivElement> {}

const commonCardStyling = 'bg-primary-std border-primary-std rounded-lg border sm:shadow-sm';

export const TSTitleCard: FC<TSTitleCardProps> = ({
  descriptor,
  longString,
  title,
  icon,
  smallContent,
  largeContent,
  primaryAction,
  secondaryAction,
  theme,
  children,
  className,
  ...props
}) => {
  return (
    <div className={merge(commonCardStyling, 'p-2', className)} {...props}>
      <div className={merge('flex flex-col', largeContent && 'gap-4')}>
        <CardHeader
          title={title}
          descriptor={descriptor}
          icon={icon}
          smallContent={smallContent}
          largeContent={largeContent}
          primaryAction={primaryAction}
          secondaryAction={secondaryAction}
          theme={theme}
          longString={longString}
        />
        <div className="lg:hidden mx-4">{largeContent}</div>
      </div>
      <div className="border-t m-4 pt-4 h-full">{children}</div>
    </div>
  );
};

export const TSCard: FC<TSCardProps> = ({ children, className, ...props }) => {
  return (
    <div className={twMerge(commonCardStyling, className)} {...props}>
      {children}
    </div>
  );
};

interface TSCardChevronRightProps {
  onClick: () => void;
  icon?: JSX.Element;
  title: string;
  description?: string;
  largeHeader?: boolean;
}
export const TSCardChevronRight: FC<TSCardChevronRightProps> = ({ onClick, icon, title, description, largeHeader }) => {
  return (
    <TSCard className="pl-6 pr-3 py-3 hover:cursor-pointer" onClick={onClick}>
      <div className="flex flex-row items-center">
        {icon}
        <div style={{ maxWidth: 'calc(100% - 35px - 40px' }} className="px-4 flex w-full flex-col">
          <span className={largeHeader ? 'text-xl font-extrabold tracking-tight sm:text-2xl' : 'font-semibold'}>
            {title}
          </span>
          <p>{description}</p>
        </div>
        <Icon className="h-[35px] w-[35px]" Icon={HiOutlineChevronRight} />
      </div>
    </TSCard>
  );
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {}

export const Card: React.FC<CardProps> = ({ className, ...props }) => {
  return (
    <div
      className={twMerge('bg-primary-std border-primary-std rounded-lg border sm:shadow-sm', className)}
      {...props}
    />
  );
};
